import PropTypes from 'prop-types';
import React from 'react';

import logger from '@/shared/utils/logger';
import { extractSafeEmbedSrc } from '@/shared/utils/urlHelpers';

import './EmbeddedMapFrame.css';

/**
 * Sandbox tokens granted to third-party map embeds.
 *
 * MEASURED — do not widen or narrow without re-measuring. Verified 2026-08-04
 * against three real catalog embeds, served over http://localhost (a file://
 * origin is not a valid test: hosts reject its null origin before sandbox is
 * ever a factor):
 *   - US Drought Monitor  droughtmonitor.unl.edu, server-rendered dashboard
 *   - Colorado Water Plan storymaps.arcgis.com
 *   - River Watch         arcgis.com/apps/MapSeries
 *
 * Results, most restrictive first:
 *
 *   sandbox=""                       All fail. "Blocked script execution ...
 *                                    the 'allow-scripts' permission is not set."
 *
 *   allow-scripts                    All fail — but they still PAINT, which
 *                                    makes this look like success. The frame is
 *                                    assigned an OPAQUE origin, so the embedded
 *                                    page's requests for its own resources
 *                                    become cross-origin and are CORS-blocked:
 *                                    "Access to CSS stylesheet at
 *                                    droughtmonitor.unl.edu/... from origin
 *                                    'null' has been blocked by CORS policy."
 *                                    Trust the console here, not the screenshot.
 *
 *   allow-scripts allow-same-origin  All three work. This is the minimum.
 *
 *   + allow-popups,                  No additional benefit. Omitted.
 *     allow-popups-to-escape-sandbox,
 *     allow-forms
 *
 *   (no sandbox attribute)           Works. Control case.
 *
 * `allow-top-navigation` is absent and must stay absent: without it the embedded
 * page cannot redirect the parent window, which is the primary threat from a
 * third-party StoryMap or dashboard.
 *
 * `allow-same-origin` keeps the framed document on its *own* origin rather than
 * an opaque one. It grants no access to our origin. It would only be dangerous
 * if the src were same-origin with us — which extractSafeEmbedSrc prevents by
 * rejecting relative and protocol-relative URLs. These two decisions hold each
 * other up; relaxing either one alone opens a sandbox escape.
 *
 * To re-measure when an embed stops rendering, use the harness at
 * docs/sandbox-probe.html — serve it over http (`npx serve docs -l 5050`), not
 * file://, or every panel fails for reasons unrelated to sandbox.
 */
export const EMBED_SANDBOX = 'allow-scripts allow-same-origin';

/** Accessible name used when the payload supplies no usable title. */
const DEFAULT_EMBED_TITLE = 'Embedded map';

/**
 * Render a third-party map embed (StoryMap, dashboard, drought monitor) inside
 * a restrictive iframe sandbox, with a plain link to the map alongside it.
 *
 * The link is always rendered, not only on failure. A cross-origin iframe whose
 * host refuses framing still fires `load`, and the same-origin policy stops us
 * inspecting the frame, so the browser gives us no reliable way to detect the
 * failure. Keeping the link present means a broken embed degrades to "empty
 * frame plus working link" rather than "empty frame".
 */
export default function EmbeddedMapFrame({
    embedCode,
    fallbackUrl,
    title,
    embeddable,
    className,
}) {
    const { src, reason } = React.useMemo(
        () => extractSafeEmbedSrc(embedCode),
        [embedCode]
    );

    const resolvedTitle = title?.trim() || DEFAULT_EMBED_TITLE;

    React.useEffect(() => {
        // Two independent diagnostics, deliberately not nested: a valid embed
        // with no title must still report the missing title.
        if (reason && reason !== 'empty') {
            logger.warn(
                { reason, title },
                'Catalog record has an unusable embed_code'
            );
        }
        if (!title?.trim()) {
            logger.warn(
                { fallbackUrl },
                'Embedded map rendered without a title'
            );
        }
    }, [reason, title, fallbackUrl]);

    // embeddable is a tri-state. false means the engine health crawl confirmed
    // this host refuses framing, so skip the request entirely. null means the
    // crawl has not run yet, and we render optimistically rather than hide a
    // map that most likely works.
    const showFrame = embeddable !== false && Boolean(src);

    return (
        <div
            className={`embedded-map-frame d-flex flex-column gap-2 ${
                className || ''
            }`.trim()}
        >
            {showFrame && (
                <iframe
                    className="embedded-map-frame__iframe"
                    src={src}
                    title={resolvedTitle}
                    sandbox={EMBED_SANDBOX}
                    loading="lazy"
                    referrerPolicy="no-referrer"
                />
            )}

            {fallbackUrl ? (
                <a
                    href={fallbackUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small"
                >
                    {`Open ${resolvedTitle} in a new tab`}
                </a>
            ) : (
                !showFrame && (
                    <p className="text-muted small mb-0">
                        Map preview unavailable.
                    </p>
                )
            )}
        </div>
    );
}

EmbeddedMapFrame.propTypes = {
    /** Raw `embed_code` HTML from the catalog; may be absent */
    embedCode: PropTypes.string,
    /** Plain URL to the map, used as both companion link and fallback */
    fallbackUrl: PropTypes.string,
    /** Accessible name; falls back to a generic label when absent */
    title: PropTypes.string,
    /** Tri-state from the engine health crawl. null means never checked */
    embeddable: PropTypes.bool,
    /** Class applied to the wrapper. The parent owns dimensions */
    className: PropTypes.string,
};
